import { createPublicClient, fallback, http } from 'viem'
import { mainnet } from 'viem/chains'
import { mainnet_config, test_net_config } from "./chain_list";
import { environment } from "src/environments/environment";
const config_chain = environment.ENVNAME == "DEVELOPMENT" ? test_net_config : mainnet_config
export const publicClient = createPublicClient({
  chain: mainnet,
  transport: http()
})

/**
 * Builds a viem transport that fans out across every RPC configured for the
 * default chain. viem's `fallback` rotates to the next endpoint on error, so a
 * single flaky provider (e.g. `rpc-amoy.polygon.technology` returning 504) no
 * longer takes the whole page down. Ordered: the dedicated `PROVIDERS` first
 * (Alchemy/Infura/dRPC), then the public `PROVIDER` as a last resort.
 */
export function createDefaultChainTransport() {
  const chainConfig = (environment as any)[environment.DEFAULT_CHAIN] ?? {};
  // Only trust string URLs — a hand-edited environment file can carry non-string
  // values, and those would blow up inside http(url, ...).
  const primary: string | undefined =
    typeof chainConfig.PROVIDER === 'string' ? chainConfig.PROVIDER : undefined;
  const providers: string[] = Array.isArray(chainConfig.PROVIDERS)
    ? chainConfig.PROVIDERS.filter((url: unknown): url is string => typeof url === 'string')
    : [];
  // Dedicated PROVIDERS first (order preserved), public PROVIDER strictly last.
  // Drop PROVIDER from the dedicated list first so it can never jump ahead
  // (staging lists it inside PROVIDERS too), then Set-dedupe the rest.
  const urls: string[] = [...new Set([
    ...providers.filter((url) => url && url !== primary),
    ...(primary ? [primary] : []),
  ])];
  if (urls.length === 0) {
    // Fail fast with a clear message instead of an opaque viem error on the first read.
    throw new Error(`No RPC URLs configured for DEFAULT_CHAIN=${environment.DEFAULT_CHAIN} (missing PROVIDER/PROVIDERS)`);
  }
  // Options mirror wagmi-config.ts: bounded per-provider timeout, no provider-level
  // retries, and ordered (rank:false) sequential failover to the next endpoint.
  return fallback(
    urls.map((url) => http(url, { batch: true, retryCount: 0, retryDelay: 0, timeout: 7000 })),
    { rank: false, retryCount: Math.max(0, urls.length - 1), retryDelay: 0 },
  );
}

/**
 * Public client for the app's default chain, backed by the fallback transport
 * above. Prefer this over a single-endpoint `http(...)` client for on-chain
 * reads so a provider outage degrades gracefully instead of failing hard.
 */
export function createDefaultChainPublicClient() {
  return createPublicClient({ transport: createDefaultChainTransport() });
}
